// =============================================================================
// CALL STORE (Zustand)
// =============================================================================

import { create } from 'zustand';
import { api } from '../api/client.js';
import { socketService } from '../api/socket.js';
import { webrtcService } from '../services/webrtc.js';
import { useAuthStore } from './authStore.js';
import type { CallType, CallStatus, IncomingCall, CallParticipant } from '@chlann/shared';

// =============================================================================
// TYPES
// =============================================================================

export interface ActiveCall {
  id: string;
  conversationId: string;
  conversationName: string | null;
  callType: CallType;
  status: CallStatus;
  initiatedBy: string;
  initiatorName: string;
  participants: CallParticipant[];
  startedAt: string | null;
  // Local media state
  isLocalVideoEnabled: boolean;
  isLocalAudioEnabled: boolean;
  isSpeakerEnabled: boolean;
  // Whether the one-tap background blur is applied to the local camera. The
  // blurred track is sent to peers via replaceTrack; this only tracks UI state.
  isBackgroundBlurred: boolean;
  // Resolved audio output device for the remote sink. Empty string = the
  // browser default device; a deviceId routes the remote audio elsewhere via
  // setSinkId. Only meaningful when more than one output device exists.
  speakerSinkId: string;
  // WebRTC state
  localStream: MediaStream | null;
  remoteStreams: Map<string, MediaStream>;
}

// An ephemeral in-call reaction, animated as an emoji burst then dropped. Both
// the sender's own tap and peers' relayed reactions land here; nothing is
// persisted. id keys the burst animation and the auto-expiry.
export interface CallReaction {
  id: string;
  emoji: string;
  fromUserId: string;
  fromSelf: boolean;
}

// A call that ended without connecting (declined or ring-timeout), surfaced as
// an honest call-back card in the conversation it belonged to.
export interface MissedCall {
  conversationId: string;
  callType: CallType;
  displayName: string;
  reason: 'missed' | 'declined';
  at: string;
}

export interface CallHistoryEntry {
  id: string;
  conversationId: string;
  conversationName: string | null;
  conversationAvatarUrl: string | null;
  callType: CallType;
  status: CallStatus;
  initiatedBy: string;
  initiatorName: string;
  participants: Array<{
    userId: string;
    displayName: string;
    avatarUrl: string | null;
    joinedAt: string | null;
    leftAt: string | null;
  }>;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  wasInitiator: boolean;
  wasAnswered: boolean;
  createdAt: string;
}

interface CallState {
  // State
  activeCall: ActiveCall | null;
  incomingCall: IncomingCall | null;
  callHistory: CallHistoryEntry[];
  // Live in-call reaction bursts (ephemeral). Each entry animates then is
  // removed by dismissReaction once its burst finishes.
  reactions: CallReaction[];
  // The most recent unanswered call, shown as a call-back card in its
  // conversation until acted on or dismissed.
  lastMissedCall: MissedCall | null;
  isLoading: boolean;
  error: string | null;

  // Real RTCPeerConnection state for the remote peer, mirrored from
  // webrtcService so the in-call UI can show an honest connection indicator.
  // Single-peer for 1-on-1 calls; the W2 group grid will make this per-user.
  connectionState: RTCPeerConnectionState | null;

  // ICE servers for WebRTC
  iceServers: RTCIceServer[];

  // Actions
  startCall: (conversationId: string, callType: CallType) => Promise<string | null>;
  answerCall: () => Promise<boolean>;
  declineCall: () => Promise<boolean>;
  hangUp: () => Promise<void>;
  toggleVideo: () => void;
  toggleAudio: () => void;
  toggleSpeaker: () => Promise<void>;
  flipCamera: () => Promise<void>;
  toggleBackgroundBlur: () => Promise<void>;
  sendReaction: (emoji: string) => void;
  dismissReaction: (id: string) => void;
  clearMissedCall: () => void;
  loadCallHistory: () => Promise<void>;
  fetchICEServers: () => Promise<void>;
  clearError: () => void;

  // Socket event handler setup
  setupCallSocketListeners: () => () => void;

  // WebSocket event handlers (internal)
  handleIncomingCall: (call: IncomingCall) => void;
  handleCallAccepted: (callId: string, userId: string) => void | Promise<void>;
  handleCallDeclined: (callId: string, userId: string) => void;
  handleCallEnded: (callId: string, reason: string, durationSeconds?: number) => void;
  handleParticipantJoined: (callId: string, participant: CallParticipant) => void;
  handleParticipantLeft: (callId: string, userId: string) => void;
  handleParticipantUpdated: (
    callId: string,
    userId: string,
    updates: Partial<Pick<CallParticipant, 'isVideoEnabled' | 'isAudioEnabled' | 'isSpeaking'>>
  ) => void;
  handleCallReaction: (callId: string, fromUserId: string, emoji: string) => void;

  // WebRTC handlers (internal)
  handleRemoteSDP: (fromUserId: string, type: 'offer' | 'answer', sdp: string) => Promise<void>;
  handleRemoteICE: (fromUserId: string, candidate: RTCIceCandidateInit) => Promise<void>;

  // Internal helpers
  cleanupCall: () => void;
  updateRemoteStream: (userId: string, stream: MediaStream) => void;
  removeRemoteStream: (userId: string) => void;
  reset: () => void;
}

// =============================================================================
// STORE
// =============================================================================

export const useCallStore = create<CallState>()((set, get) => ({
  // Initial state
  activeCall: null,
  incomingCall: null,
  callHistory: [],
  reactions: [],
  lastMissedCall: null,
  isLoading: false,
  error: null,
  connectionState: null,
  iceServers: [],

  // ---------------------------------------------------------------------------
  // Start a new call (initiator flow)
  // ---------------------------------------------------------------------------
  startCall: async (conversationId, callType) => {
    set({ isLoading: true, error: null, connectionState: null });

    try {
      // Fetch ICE servers if needed
      if (get().iceServers.length === 0) {
        await get().fetchICEServers();
      }

      const { iceServers } = get();

      // Acquire local media first
      const localStream = await webrtcService.acquireLocalMedia(callType === 'video');

      // Configure webrtcService
      webrtcService.setIceServers(iceServers);
      webrtcService.setCallbacks({
        onRemoteStream: (userId, stream) => { get().updateRemoteStream(userId, stream); },
        onRemoteStreamRemoved: (userId) => { get().removeRemoteStream(userId); },
        onConnectionStateChange: (_userId, state) => { set({ connectionState: state }); },
        onIceConnectionStateChange: () => {},
      });

      // Initiate call via API
      const result = await api.initiateCall({ conversationId, callType });

      if (result.error) {
        webrtcService.cleanup();
        set({ isLoading: false, error: result.error.message });
        return null;
      }

      if (result.data) {
        const call = result.data.call;

        // Set call ID in webrtcService for signaling
        webrtcService.setCurrentCallId(call.id);

        set({
          isLoading: false,
          activeCall: {
            id: call.id,
            conversationId: call.conversationId,
            conversationName: null,
            callType: call.callType,
            status: 'initiating',
            initiatedBy: '',
            initiatorName: '',
            // The POST /api/calls response only returns { call } - it omits the
            // participants array the answerer gets over the socket. Guard against
            // that so the initiator still enters the call (otherwise .map throws
            // and startCall fails); the roster fills in via call:participant-joined.
            participants: (result.data.participants ?? []).map((p) => ({
              userId: p.userId,
              displayName: p.displayName,
              avatarUrl: p.avatarUrl,
              joinedAt: null,
              leftAt: null,
              isVideoEnabled: false,
              // A call begins with the microphone live; call:participant-updated
              // flips this to false only when the peer actually mutes. Seeding
              // false made every remote look muted before any toggle arrived.
              isAudioEnabled: true,
              isSpeaking: false,
              connectionQuality: 'unknown' as const,
            })),
            startedAt: null,
            isLocalVideoEnabled: callType === 'video',
            isLocalAudioEnabled: true,
            isSpeakerEnabled: true,
            isBackgroundBlurred: false,
            speakerSinkId: '',
            localStream,
            remoteStreams: new Map(),
          },
          incomingCall: null,
          reactions: [],
        });

        return call.id;
      }

      webrtcService.cleanup();
      set({ isLoading: false, error: 'Failed to initiate call' });
      return null;
    } catch (err) {
      webrtcService.cleanup();
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to start call',
      });
      return null;
    }
  },

  // ---------------------------------------------------------------------------
  // Answer incoming call (receiver flow)
  // ---------------------------------------------------------------------------
  answerCall: async () => {
    const { incomingCall } = get();

    if (!incomingCall) {
      set({ error: 'No incoming call to answer' });
      return false;
    }

    set({ isLoading: true, error: null, connectionState: null });

    try {
      // Fetch ICE servers if needed
      if (get().iceServers.length === 0) {
        await get().fetchICEServers();
      }

      const { iceServers } = get();

      // Acquire local media
      const localStream = await webrtcService.acquireLocalMedia(
        incomingCall.callType === 'video'
      );

      // Configure webrtcService
      webrtcService.setIceServers(iceServers);
      webrtcService.setCurrentCallId(incomingCall.id);
      webrtcService.setCallbacks({
        onRemoteStream: (userId, stream) => { get().updateRemoteStream(userId, stream); },
        onRemoteStreamRemoved: (userId) => { get().removeRemoteStream(userId); },
        onConnectionStateChange: (_userId, state) => { set({ connectionState: state }); },
        onIceConnectionStateChange: () => {},
      });

      // Accept call via API
      const result = await api.acceptCall(incomingCall.id);

      if (result.error) {
        webrtcService.cleanup();
        set({ isLoading: false, error: result.error.message });
        return false;
      }

      // Convert incoming call to active call
      set({
        isLoading: false,
        activeCall: {
          id: incomingCall.id,
          conversationId: incomingCall.conversationId,
          conversationName: incomingCall.conversationName,
          callType: incomingCall.callType,
          status: 'connecting',
          initiatedBy: incomingCall.initiatedBy,
          initiatorName: incomingCall.initiatorName,
          participants: incomingCall.participants.map((p) => ({
            userId: p.userId,
            displayName: p.displayName,
            avatarUrl: p.avatarUrl,
            joinedAt: null,
            leftAt: null,
            isVideoEnabled: false,
            // Audio is live at the start of a call; only a real mute toggle
            // (call:participant-updated) flips it false and shows the badge.
            isAudioEnabled: true,
            isSpeaking: false,
            connectionQuality: 'unknown' as const,
          })),
          startedAt: null,
          isLocalVideoEnabled: incomingCall.callType === 'video',
          isLocalAudioEnabled: true,
          isSpeakerEnabled: true,
          isBackgroundBlurred: false,
          speakerSinkId: '',
          localStream,
          remoteStreams: new Map(),
        },
        incomingCall: null,
        reactions: [],
      });

      // Notify via socket that we accepted
      socketService.acceptCallSocket(incomingCall.id);

      return true;
    } catch (err) {
      webrtcService.cleanup();
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to answer call',
      });
      return false;
    }
  },

  // ---------------------------------------------------------------------------
  // Decline incoming call
  // ---------------------------------------------------------------------------
  declineCall: async () => {
    const { incomingCall } = get();

    if (!incomingCall) {
      return false;
    }

    set({ isLoading: true, error: null });

    const result = await api.declineCall(incomingCall.id);

    if (result.error) {
      set({ isLoading: false, error: result.error.message });
      return false;
    }

    // Notify via socket
    socketService.declineCallSocket(incomingCall.id);

    set({
      isLoading: false,
      incomingCall: null,
    });

    return true;
  },

  // ---------------------------------------------------------------------------
  // Hang up current call
  // ---------------------------------------------------------------------------
  hangUp: async () => {
    const { activeCall } = get();
    if (!activeCall) return;

    set({ isLoading: true });

    // Notify server
    await api.endCall(activeCall.id);

    // Notify via socket
    socketService.leaveCall(activeCall.id);

    // Cleanup
    get().cleanupCall();
    set({ isLoading: false, activeCall: null });
  },

  // ---------------------------------------------------------------------------
  // Toggle local video
  // ---------------------------------------------------------------------------
  toggleVideo: () => {
    const { activeCall } = get();
    if (!activeCall) return;

    const newState = !activeCall.isLocalVideoEnabled;

    // Toggle via webrtcService
    webrtcService.toggleLocalVideo(newState);

    // Notify others via socket
    socketService.toggleVideoSocket(activeCall.id, newState);

    set({
      activeCall: {
        ...activeCall,
        isLocalVideoEnabled: newState,
      },
    });
  },

  // ---------------------------------------------------------------------------
  // Toggle local audio
  // ---------------------------------------------------------------------------
  toggleAudio: () => {
    const { activeCall } = get();
    if (!activeCall) return;

    const newState = !activeCall.isLocalAudioEnabled;

    // Toggle via webrtcService
    webrtcService.toggleLocalAudio(newState);

    // Notify others via socket
    socketService.toggleAudioSocket(activeCall.id, newState);

    set({
      activeCall: {
        ...activeCall,
        isLocalAudioEnabled: newState,
      },
    });
  },

  // ---------------------------------------------------------------------------
  // Toggle speaker - routes the remote audio to a real output device.
  //
  // Speaker on  -> the browser default device (loudspeaker on a phone).
  // Speaker off -> the next available output device (an earpiece / headset),
  //                when one exists. With only the default device there is
  //                nowhere else to send the audio, so the flag still flips but
  //                the sink stays empty and useRemoteMedia keeps the default.
  // ---------------------------------------------------------------------------
  toggleSpeaker: async () => {
    const { activeCall } = get();
    if (!activeCall) return;

    const nextEnabled = !activeCall.isSpeakerEnabled;

    let sinkId = '';
    if (!nextEnabled) {
      const outputs = await webrtcService.getAudioOutputDevices();
      const alternate = outputs.find(
        (d) => d.deviceId && d.deviceId !== 'default'
      );
      if (alternate) {
        sinkId = alternate.deviceId;
      }
    }

    set({
      activeCall: {
        ...get().activeCall!,
        isSpeakerEnabled: nextEnabled,
        speakerSinkId: sinkId,
      },
    });
  },

  // ---------------------------------------------------------------------------
  // Flip camera - swap the local video track in place via replaceTrack
  // ---------------------------------------------------------------------------
  flipCamera: async () => {
    const { activeCall } = get();
    if (!activeCall || activeCall.callType !== 'video') return;

    try {
      const updatedStream = await webrtcService.flipCamera();
      if (updatedStream) {
        // localStream is the same MediaStream object (tracks swapped in place);
        // re-set it so the self-view effect re-binds to the new track.
        set({
          activeCall: {
            ...get().activeCall!,
            localStream: updatedStream,
          },
        });
      }
    } catch {
      // A flip failure (no second camera, permission revoked) is non-fatal -
      // the existing camera keeps streaming.
    }
  },

  // ---------------------------------------------------------------------------
  // Toggle one-tap background blur on the local camera (video calls only). The
  // blurred track is swapped into the peer connections via replaceTrack, so the
  // remote side receives it without renegotiation.
  // ---------------------------------------------------------------------------
  toggleBackgroundBlur: async () => {
    const { activeCall } = get();
    if (!activeCall || activeCall.callType !== 'video') return;

    const next = !activeCall.isBackgroundBlurred;
    try {
      const updatedStream = await webrtcService.toggleBackgroundBlur(next);
      set({
        activeCall: {
          ...get().activeCall!,
          isBackgroundBlurred: webrtcService.isBlurEnabled(),
          // The local stream's video track was swapped in place; re-set it so
          // the self-view effect re-binds to the new (blurred / restored) track.
          localStream: updatedStream ?? get().activeCall!.localStream,
        },
      });
    } catch {
      // A blur failure (unsupported canvas capture) is non-fatal - the camera
      // keeps streaming unblurred and the toggle reflects the real state.
      set({
        activeCall: {
          ...get().activeCall!,
          isBackgroundBlurred: webrtcService.isBlurEnabled(),
        },
      });
    }
  },

  // ---------------------------------------------------------------------------
  // Send an ephemeral in-call reaction. Relays over the authorized call room
  // and shows our own burst immediately (optimistic, not echoed back).
  // ---------------------------------------------------------------------------
  sendReaction: (emoji) => {
    const { activeCall } = get();
    if (!activeCall) return;

    socketService.sendCallReaction(activeCall.id, emoji);

    const selfId = useAuthStore.getState().user?.id ?? '';
    const reaction: CallReaction = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      emoji,
      fromUserId: selfId,
      fromSelf: true,
    };
    set({ reactions: [...get().reactions, reaction] });
  },

  // ---------------------------------------------------------------------------
  // Remove a reaction once its burst animation has finished.
  // ---------------------------------------------------------------------------
  dismissReaction: (id) => {
    set({ reactions: get().reactions.filter((r) => r.id !== id) });
  },

  // ---------------------------------------------------------------------------
  // Clear the missed-call card
  // ---------------------------------------------------------------------------
  clearMissedCall: () => { set({ lastMissedCall: null }); },

  // ---------------------------------------------------------------------------
  // Load call history
  // ---------------------------------------------------------------------------
  loadCallHistory: async () => {
    set({ isLoading: true, error: null });

    const result = await api.getCallHistory({ limit: 50 });

    if (result.error) {
      set({ isLoading: false, error: result.error.message });
      return;
    }

    if (result.data) {
      set({
        isLoading: false,
        callHistory: result.data.calls.map((c) => ({
          ...c,
          callType: c.callType,
          status: c.status as CallStatus,
        })),
      });
    }
  },

  // ---------------------------------------------------------------------------
  // Fetch ICE/TURN servers
  // ---------------------------------------------------------------------------
  fetchICEServers: async () => {
    const result = await api.getTURNCredentials();

    if (result.data) {
      const servers = result.data.iceServers.map((s) => ({
        urls: s.urls,
        username: s.username,
        credential: s.credential,
      }));

      set({ iceServers: servers });
      webrtcService.setIceServers(servers);
    }
  },

  // ---------------------------------------------------------------------------
  // Clear error
  // ---------------------------------------------------------------------------
  clearError: () => { set({ error: null }); },

  // ---------------------------------------------------------------------------
  // Setup socket event listeners for calls
  // ---------------------------------------------------------------------------
  setupCallSocketListeners: () => {
    const socket = socketService.getSocket();
    if (!socket) {
      return () => {};
    }

    // Incoming call
    const handleIncoming = (data: IncomingCall) => {
      get().handleIncomingCall(data);
    };

    // Call accepted by remote
    const handleAccepted = (data: { callId: string; userId: string }) => {
      void get().handleCallAccepted(data.callId, data.userId);
    };

    // Call declined by remote
    const handleDeclined = (data: { callId: string; userId: string }) => {
      get().handleCallDeclined(data.callId, data.userId);
    };

    // Call ended
    const handleEnded = (data: { callId: string; reason: string; durationSeconds?: number }) => {
      get().handleCallEnded(data.callId, data.reason, data.durationSeconds);
    };

    // Participant joined
    const handleJoined = (data: { callId: string; participant: CallParticipant }) => {
      get().handleParticipantJoined(data.callId, data.participant);
    };

    // Participant left
    const handleLeft = (data: { callId: string; userId: string }) => {
      get().handleParticipantLeft(data.callId, data.userId);
    };

    // Participant updated (audio/video toggle)
    const handleUpdated = (data: {
      callId: string;
      userId: string;
      isVideoEnabled?: boolean;
      isAudioEnabled?: boolean;
      isSpeaking?: boolean;
    }) => {
      get().handleParticipantUpdated(data.callId, data.userId, {
        isVideoEnabled: data.isVideoEnabled,
        isAudioEnabled: data.isAudioEnabled,
        isSpeaking: data.isSpeaking,
      });
    };

    // WebRTC SDP (offer/answer)
    const handleSDP = async (data: {
      fromUserId: string;
      type: 'offer' | 'answer';
      sdp: string;
    }) => {
      await get().handleRemoteSDP(data.fromUserId, data.type, data.sdp);
    };

    // WebRTC ICE candidate
    const handleICE = async (data: { fromUserId: string; candidate: RTCIceCandidateInit }) => {
      await get().handleRemoteICE(data.fromUserId, data.candidate);
    };

    // Ephemeral in-call reaction from a peer
    const handleReaction = (data: {
      callId: string;
      fromUserId: string;
      fromUserName: string;
      emoji: string;
    }) => {
      get().handleCallReaction(data.callId, data.fromUserId, data.emoji);
    };

    // Register listeners
    socket.on('call:incoming', handleIncoming);
    socket.on('call:accepted', handleAccepted);
    socket.on('call:declined', handleDeclined);
    socket.on('call:ended', handleEnded);
    socket.on('call:participant-joined', handleJoined);
    socket.on('call:participant-left', handleLeft);
    socket.on('call:participant-updated', handleUpdated);
    socket.on('call:sdp', handleSDP);
    socket.on('call:ice-candidate', handleICE);
    socket.on('call:reaction', handleReaction);

    // Return cleanup function
    return () => {
      socket.off('call:incoming', handleIncoming);
      socket.off('call:accepted', handleAccepted);
      socket.off('call:declined', handleDeclined);
      socket.off('call:ended', handleEnded);
      socket.off('call:participant-joined', handleJoined);
      socket.off('call:participant-left', handleLeft);
      socket.off('call:participant-updated', handleUpdated);
      socket.off('call:sdp', handleSDP);
      socket.off('call:ice-candidate', handleICE);
      socket.off('call:reaction', handleReaction);
    };
  },

  // ---------------------------------------------------------------------------
  // WebSocket: Handle incoming call
  // ---------------------------------------------------------------------------
  handleIncomingCall: (call) => {
    // Don't interrupt an active call
    if (get().activeCall) {
      return;
    }

    set({ incomingCall: call });
  },

  // ---------------------------------------------------------------------------
  // WebSocket: Handle call accepted (remote user accepted our call)
  // ---------------------------------------------------------------------------
  handleCallAccepted: async (callId, userId) => {
    const { activeCall } = get();
    if (!activeCall || activeCall.id !== callId) return;

    // Update participant status
    set({
      activeCall: {
        ...activeCall,
        status: 'connecting',
        participants: activeCall.participants.map((p) =>
          p.userId === userId ? { ...p, joinedAt: new Date().toISOString() } : p
        ),
      },
    });

    // Create WebRTC offer for the user who accepted
    try {
      await webrtcService.createOffer(userId);
    } catch {
      // Offer creation failure is handled by WebRTC connection state
    }
  },

  // ---------------------------------------------------------------------------
  // WebSocket: Handle call declined
  // ---------------------------------------------------------------------------
  handleCallDeclined: (callId, userId) => {
    const { activeCall } = get();
    if (!activeCall || activeCall.id !== callId) return;

    // Remove declined participant
    const declinedParticipant = activeCall.participants.find((p) => p.userId === userId);
    const remainingParticipants = activeCall.participants.filter((p) => p.userId !== userId);

    // Close peer connection for this user
    webrtcService.closePeerConnection(userId);

    if (remainingParticipants.length === 0) {
      // No one left, end call. Surface a call-back card in the conversation so
      // the unanswered call is honestly visible, not silently dropped.
      get().cleanupCall();
      set({
        activeCall: null,
        lastMissedCall: {
          conversationId: activeCall.conversationId,
          callType: activeCall.callType,
          displayName: declinedParticipant?.displayName || activeCall.participants[0]?.displayName || '',
          reason: 'declined',
          at: new Date().toISOString(),
        },
      });
    } else {
      set({
        activeCall: {
          ...activeCall,
          participants: remainingParticipants,
        },
      });
    }
  },

  // ---------------------------------------------------------------------------
  // WebSocket: Handle call ended
  // ---------------------------------------------------------------------------
  handleCallEnded: (callId, reason, _durationSeconds) => {
    const { activeCall, incomingCall } = get();

    if (activeCall?.id === callId) {
      // An outgoing call the other side declined, or a ring that timed out
      // before connecting, surfaces as a call-back card in the conversation -
      // the unanswered call is shown honestly rather than silently dropped. A
      // call that actually connected (status active) just ends with no card.
      const neverConnected = activeCall.status !== 'active';
      const missedReason = reason === 'declined' || reason === 'missed';
      get().cleanupCall();
      set({
        activeCall: null,
        ...(neverConnected && missedReason
          ? {
              lastMissedCall: {
                conversationId: activeCall.conversationId,
                callType: activeCall.callType,
                displayName: activeCall.participants[0]?.displayName || '',
                reason: reason === 'declined' ? 'declined' : 'missed',
                at: new Date().toISOString(),
              },
            }
          : {}),
      });
    }

    // An incoming call that ends before we answer (ring-timeout or caller hung
    // up) is a missed call. Record a call-back card in its conversation so the
    // missed call is honestly surfaced, then clear the ringing modal.
    if (incomingCall?.id === callId) {
      if (reason === 'missed' || reason === 'ended') {
        set({
          lastMissedCall: {
            conversationId: incomingCall.conversationId,
            callType: incomingCall.callType,
            displayName: incomingCall.initiatorName,
            reason: 'missed',
            at: new Date().toISOString(),
          },
        });
      }
      set({ incomingCall: null });
    }
  },

  // ---------------------------------------------------------------------------
  // WebSocket: Handle participant joined
  // ---------------------------------------------------------------------------
  handleParticipantJoined: (callId, participant) => {
    const { activeCall } = get();
    if (!activeCall || activeCall.id !== callId) return;

    // Update or add participant
    const existingIndex = activeCall.participants.findIndex(
      (p) => p.userId === participant.userId
    );

    let updatedParticipants: CallParticipant[];
    if (existingIndex >= 0) {
      updatedParticipants = [...activeCall.participants];
      updatedParticipants[existingIndex] = participant;
    } else {
      updatedParticipants = [...activeCall.participants, participant];
    }

    set({
      activeCall: {
        ...activeCall,
        status: 'active',
        startedAt: activeCall.startedAt || new Date().toISOString(),
        participants: updatedParticipants,
      },
    });
  },

  // ---------------------------------------------------------------------------
  // WebSocket: Handle participant left
  // ---------------------------------------------------------------------------
  handleParticipantLeft: (callId, userId) => {
    const { activeCall } = get();
    if (!activeCall || activeCall.id !== callId) return;

    // Close peer connection for this user
    webrtcService.closePeerConnection(userId);

    const updatedParticipants = activeCall.participants.map((p) =>
      p.userId === userId ? { ...p, leftAt: new Date().toISOString() } : p
    );

    // Check if we're the only one left
    const activeParticipants = updatedParticipants.filter((p) => !p.leftAt);
    if (activeParticipants.length <= 1) {
      // Only us left, end call
      get().cleanupCall();
      set({ activeCall: null });
    } else {
      set({
        activeCall: {
          ...activeCall,
          participants: updatedParticipants,
          remoteStreams: new Map(activeCall.remoteStreams), // Force update
        },
      });
    }
  },

  // ---------------------------------------------------------------------------
  // WebSocket: Handle participant updated
  // ---------------------------------------------------------------------------
  handleParticipantUpdated: (callId, userId, updates) => {
    const { activeCall } = get();
    if (!activeCall || activeCall.id !== callId) return;

    const updatedParticipants = activeCall.participants.map((p) =>
      p.userId === userId ? { ...p, ...updates } : p
    );

    set({
      activeCall: {
        ...activeCall,
        participants: updatedParticipants,
      },
    });
  },

  // ---------------------------------------------------------------------------
  // WebSocket: Handle an ephemeral in-call reaction from a peer
  // ---------------------------------------------------------------------------
  handleCallReaction: (callId, fromUserId, emoji) => {
    const { activeCall } = get();
    if (!activeCall || activeCall.id !== callId) return;

    const reaction: CallReaction = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      emoji,
      fromUserId,
      fromSelf: false,
    };
    set({ reactions: [...get().reactions, reaction] });
  },

  // ---------------------------------------------------------------------------
  // WebRTC: Handle remote SDP (offer or answer)
  // ---------------------------------------------------------------------------
  handleRemoteSDP: async (fromUserId, type, sdp) => {
    const { activeCall } = get();
    if (!activeCall) {
      return;
    }

    try {
      if (type === 'offer') {
        await webrtcService.handleRemoteOffer(fromUserId, sdp);
      } else {
        await webrtcService.handleRemoteAnswer(fromUserId, sdp);
      }

      // Update call status to active once we have SDP exchange
      if (activeCall.status !== 'active') {
        set({
          activeCall: {
            ...activeCall,
            status: 'active',
            startedAt: activeCall.startedAt || new Date().toISOString(),
          },
        });
      }
    } catch {
      // SDP handling errors are non-fatal
    }
  },

  // ---------------------------------------------------------------------------
  // WebRTC: Handle remote ICE candidate
  // ---------------------------------------------------------------------------
  handleRemoteICE: async (fromUserId, candidate) => {
    try {
      await webrtcService.handleRemoteICE(fromUserId, candidate);
    } catch {
      // ICE handling errors are non-fatal
    }
  },

  // ---------------------------------------------------------------------------
  // Internal: Cleanup call resources
  // ---------------------------------------------------------------------------
  cleanupCall: () => {
    // Cleanup webrtcService (stops tracks, closes connections)
    webrtcService.cleanup();
    set({ connectionState: null, reactions: [] });
  },

  // ---------------------------------------------------------------------------
  // Reset store on logout (shared-device privacy). Releases any live
  // camera/mic tracks and peer connections first, then restores initial
  // state - including iceServers, which hold per-user TURN credentials.
  // ---------------------------------------------------------------------------
  reset: () => {
    webrtcService.cleanup();
    set({
      activeCall: null,
      incomingCall: null,
      callHistory: [],
      reactions: [],
      lastMissedCall: null,
      isLoading: false,
      error: null,
      connectionState: null,
      iceServers: [],
    });
  },

  // ---------------------------------------------------------------------------
  // Internal: Update remote stream (callback from webrtcService)
  // ---------------------------------------------------------------------------
  updateRemoteStream: (userId, stream) => {
    const { activeCall } = get();
    if (!activeCall) return;

    const newRemoteStreams = new Map(activeCall.remoteStreams);
    newRemoteStreams.set(userId, stream);

    set({
      activeCall: {
        ...activeCall,
        remoteStreams: newRemoteStreams,
      },
    });
  },

  // ---------------------------------------------------------------------------
  // Internal: Remove remote stream (callback from webrtcService)
  // ---------------------------------------------------------------------------
  removeRemoteStream: (userId) => {
    const { activeCall } = get();
    if (!activeCall) return;

    const newRemoteStreams = new Map(activeCall.remoteStreams);
    newRemoteStreams.delete(userId);

    set({
      activeCall: {
        ...activeCall,
        remoteStreams: newRemoteStreams,
      },
    });
  },
}));
